import { SetPagedRequest, SetPagedResponse } from "modules/cards/model/set";
import { useInfiniteQuery } from "@tanstack/react-query";
import { QUERIES } from "modules/common/query";
import { API_ROUTES } from "utils/api-route.constants";
import { useFetchWrapper } from "modules/common/hooks/useFetchWrapper";
import { useEffect, useRef } from "react";
import {
  INFINITE_STATIC_DATA_CONFIG,
  keepPreviousDataScopedTo,
} from "modules/common/react-query-configs";
import {
  appendPricingParams,
  usePricingRequest,
  withPricingCacheKey,
} from "modules/common/hooks/usePricingRequest";

export const useInfiniteCardSets = (req: SetPagedRequest) => {
  const fetch = useFetchWrapper();
  const loadMoreRef = useRef(null);
  const { pricing, cacheKey, isAuthReady } = usePricingRequest();

  const query = useInfiniteQuery<SetPagedResponse>({
    queryKey: withPricingCacheKey([QUERIES.SETS_PAGED, req], cacheKey),
    queryFn: ({ pageParam }) =>
      fetchSets({
        ...req,
        page: pageParam as number,
      }),
    initialPageParam: 1,
    enabled: isAuthReady && !!pricing,
    ...INFINITE_STATIC_DATA_CONFIG, // 30min stale, no refetch - perfect for set browsing

    // Hold the previous rows while a re-keyed search/sort fetches, but only
    // within the same game — see react-query-configs.ts. Bare, this renders the
    // previous game's sets under the new game's heading on a game→game switch.
    //
    // `req` is at index 1, not last: withPricingCacheKey appends the pricing key
    // after it whenever there is one.
    placeholderData: keepPreviousDataScopedTo<SetPagedRequest>(
      (previousReq) => previousReq?.game === req.game,
      1
    ),
    getNextPageParam: (lastPage, pages) => {
      if (lastPage.hasMore) {
        return pages.length + 1;
      }
      return undefined;
    },
  });

  useEffect(() => {
    if (!query.hasNextPage) {
      return;
    }

    const observer = new IntersectionObserver((entries) =>
      entries.forEach((entry) => {
        if (entry.isIntersecting && !query.isFetchingNextPage) {
          query.fetchNextPage();
        }
      })
    );

    const el = loadMoreRef && loadMoreRef.current;
    if (!el) {
      return;
    }
    observer.observe(el);
    return () => {
      observer.unobserve(el);
    };
  }, [query]);

  return {
    query,
    loadMoreRef,
  };

  function fetchSets(req: SetPagedRequest) {
    const q = new URLSearchParams({
      page: String(req.page),
      per_page: String(req.perPage),
      query: req.query,
      sort: req.sort,
      order: req.order,
    });
    appendPricingParams(q, pricing);
    const get =
      req.game && pricing?.market && pricing.currency
        ? fetch.publicGet
        : fetch.get;
    return get(`${API_ROUTES.SETS.PAGED(req.game)}?${q.toString()}`, null).then(
      (res: SetPagedResponse) => res
    );
  }
};
